import { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  FunnelIcon,
  ShoppingCartIcon,
  SwatchIcon,
  CubeIcon,
  ArchiveBoxIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  UserGroupIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import { productsApi, projectsApi } from '@/services/api';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '@/components/common';
import type {
  VidaProduct,
  ProductStats,
  ProductStatus,
} from '@/types/product';
import { TIPO_PRODUCTO_LABELS } from '@/types/product';
import ProductWizard from '@/components/ProductWizard';
import ImportProductsModal from '@/components/products/ImportProductsModal';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    draft: 'bg-yellow-100 text-yellow-700',
    inactive: 'bg-gray-100 text-gray-600',
    archived: 'bg-red-100 text-red-600',
  };
  const labels: Record<string, string> = {
    active: 'Activo',
    draft: 'Borrador',
    inactive: 'Inactivo',
    archived: 'Archivado',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.inactive}`}
    >
      {labels[status] || status}
    </span>
  );
}

function ActivationBadge({ active, label }: { active: boolean; label: string }) {
  return active ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
      {label}
    </span>
  ) : null;
}

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    simple: 'bg-vida-100 text-vida-700',
    composite: 'bg-purple-100 text-purple-700',
    collaborative: 'bg-teal-100 text-teal-700',
  };
  const labels: Record<string, string> = {
    simple: 'Simple',
    composite: 'Compuesto',
    collaborative: 'Colaborativo',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[type] || styles.simple}`}
    >
      {labels[type] || type}
    </span>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ProductsPage() {
  const { hasPermission } = useAuthStore();

  // Data
  const [products, setProducts] = useState<VidaProduct[]>([]);
  const [stats, setStats] = useState<ProductStats | null>(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProductStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tipoProductoFilter, setTipoProductoFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  // Filtro canal: '' = todos, 'catalog' = catalog_active, 'ecommerce' = ecommerce_active.
  // Sin opciones combinadas — un producto se filtra por estar en uno u otro canal.
  const [channelFilter, setChannelFilter] = useState<'' | 'catalog' | 'ecommerce'>('');
  // Sort: clic en cabecera ciclica entre desc → asc → desc.
  const [sortBy, setSortBy] = useState<'name' | 'tipoProducto' | 'pvpFinal' | 'updated_at'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Projects for filter
  const [projectsList, setProjectsList] = useState<{ _id: string; name: string }[]>([]);

  // UI State
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<VidaProduct | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canCreate = hasPermission(PERMISSIONS.PRODUCTS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.PRODUCTS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.PRODUCTS_DELETE);
  const canPublish = hasPermission(PERMISSIONS.PRODUCTS_PUBLISH);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const filters: Record<string, unknown> = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.type = typeFilter;
      if (tipoProductoFilter) filters.tipoProducto = tipoProductoFilter;
      if (projectFilter) filters.project_id = projectFilter;
      // Mapeo del canal: filtra por presencia en el canal sin importar el otro.
      // Un producto en ambos canales aparece en los dos filtros — el editor decide.
      if (channelFilter === 'catalog') filters.catalog_active = true;
      else if (channelFilter === 'ecommerce') filters.ecommerce_active = true;
      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;

      const res = await productsApi.getProducts(page, 20, filters as Record<string, string>);
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Error cargando productos');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter, tipoProductoFilter, projectFilter, channelFilter, sortBy, sortOrder, page]);

  const loadStats = useCallback(async () => {
    try {
      const res = await productsApi.stats();
      setStats(res.data);
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await projectsApi.list(1, 100, { status: 'active' });
        setProjectsList(res.data.projects || []);
      } catch {
        // Projects might not be available
      }
    };
    loadProjects();
  }, []);

  const getProjectName = (projectId?: string) => {
    if (!projectId) return '—';
    return projectsList.find((p) => p._id === projectId)?.name || '—';
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este producto?')) return;
    try {
      setDeletingId(id);
      await productsApi.deleteProduct(id);
      toast.success('Producto eliminado');
      loadProducts();
      loadStats();
    } catch {
      toast.error('Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await productsApi.changeStatus(id, status);
      toast.success('Estado actualizado');
      loadProducts();
      loadStats();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const handleWizardClose = (saved?: boolean) => {
    setWizardOpen(false);
    setEditingProduct(null);
    if (saved) {
      loadProducts();
      loadStats();
    }
  };

  const handleEdit = (product: VidaProduct) => {
    setEditingProduct(product);
    setWizardOpen(true);
  };

  // Cabeceras ordenables: clic sobre la misma columna invierte direccion;
  // clic sobre otra resetea a desc para esa columna.
  const toggleSort = (col: 'name' | 'tipoProducto' | 'pvpFinal') => {
    if (sortBy === col) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const sortArrow = (col: 'name' | 'tipoProducto' | 'pvpFinal') => {
    if (sortBy !== col) return '↕';
    return sortOrder === 'desc' ? '↓' : '↑';
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (wizardOpen) {
    return (
      <ProductWizard
        product={editingProduct}
        onClose={handleWizardClose}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Productos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de productos VIDA — {total} producto{total !== 1 ? 's' : ''}
          </p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
              Importar
            </button>
            <button
              onClick={() => setWizardOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-vida-600 text-white rounded-lg hover:bg-vida-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Nuevo Producto
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
          <StatCard label="Total" value={stats.total} icon={CubeIcon} color="bg-gray-100 text-gray-600" />
          <StatCard label="Activos" value={stats.active} icon={EyeIcon} color="bg-green-100 text-green-600" />
          <StatCard label="Borrador" value={stats.draft} icon={PencilSquareIcon} color="bg-yellow-100 text-yellow-600" />
          <StatCard label="Inactivos" value={stats.inactive} icon={ArchiveBoxIcon} color="bg-gray-100 text-gray-500" />
          <StatCard label="Archivados" value={stats.archived} icon={ArchiveBoxIcon} color="bg-red-100 text-red-500" />
          <StatCard label="Compuestos" value={stats.composite} icon={Squares2X2Icon} color="bg-purple-100 text-purple-600" />
          <StatCard label="Colaborativos" value={stats.collaborative} icon={UserGroupIcon} color="bg-teal-100 text-teal-600" />
          <StatCard label="Ecommerce" value={stats.ecommerce_active} icon={ShoppingCartIcon} color="bg-blue-100 text-blue-600" />
          <StatCard label="Catálogo" value={stats.catalog_active} icon={SwatchIcon} color="bg-cyan-100 text-cyan-600" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vida-500 focus:border-vida-500"
            />
          </div>
          <select
            value={channelFilter}
            onChange={(e) => {
              setChannelFilter(e.target.value as typeof channelFilter);
              setPage(1);
            }}
            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-vida-500"
            aria-label="Canal"
          >
            <option value="">Todos los canales</option>
            <option value="catalog">Catalogo</option>
            <option value="ecommerce">Ecommerce</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ProductStatus | '');
              setPage(1);
            }}
            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-vida-500"
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
            <option value="archived">Archivado</option>
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
              showFilters ? 'border-vida-500 text-vida-600 bg-vida-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            Filtros
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-3">
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Todos los tipos</option>
              <option value="simple">Simple</option>
              <option value="composite">Compuesto</option>
              <option value="collaborative">Colaborativo</option>
            </select>
            <select
              value={tipoProductoFilter}
              onChange={(e) => {
                setTipoProductoFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Todas las clasificaciones</option>
              {Object.entries(TIPO_PRODUCTO_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            {projectsList.length > 0 && (
              <select
                value={projectFilter}
                onChange={(e) => {
                  setProjectFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Todos los proyectos</option>
                {projectsList.map((proj) => (
                  <option key={proj._id} value={proj._id}>
                    {proj.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* Product List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <CubeIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No hay productos</p>
            <p className="text-sm mt-1">
              {search || statusFilter || typeFilter
                ? 'No se encontraron productos con los filtros seleccionados'
                : 'Crea tu primer producto para empezar'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700"
                    onClick={() => toggleSort('name')}
                  >
                    Producto <span className="ml-1 text-gray-400">{sortArrow('name')}</span>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700"
                    onClick={() => toggleSort('tipoProducto')}
                  >
                    Tipo <span className="ml-1 text-gray-400">{sortArrow('tipoProducto')}</span>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700"
                    onClick={() => toggleSort('pvpFinal')}
                  >
                    Precio <span className="ml-1 text-gray-400">{sortArrow('pvpFinal')}</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Stock
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Proyecto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Canales
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map((product) => (
                  <tr
                    key={product._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleEdit(product)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {product.mainImage ? (
                          <img
                            src={product.mainImage}
                            alt={product.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center">
                            <CubeIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                          {product.tipoProducto && (
                            <p className="text-xs text-gray-500">
                              {TIPO_PRODUCTO_LABELS[product.tipoProducto] || product.tipoProducto}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <TypeBadge type={product.type} />
                        {product.type === 'composite' && product.components?.length > 0 && (
                          <span className="text-xs text-purple-500">
                            ({product.components.length})
                          </span>
                        )}
                        {product.type === 'collaborative' && product.collaborativeSettings?.collaborators?.length > 0 && (
                          <span className="text-xs text-teal-500">
                            ({product.collaborativeSettings.collaborators.length})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        {product.pvpFinal > 0
                          ? `${product.pvpFinal.toFixed(2)} €`
                          : product.price > 0
                            ? `${product.price.toFixed(2)} €`
                            : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-sm ${
                          product.stock <= 0
                            ? 'text-red-600 font-medium'
                            : product.stock <= (product.stockMinimo || 5)
                              ? 'text-yellow-600'
                              : 'text-gray-700'
                        }`}
                      >
                        {product.stock}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={product.status} />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 truncate max-w-[120px] block">
                        {getProjectName(product.project_id)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <ActivationBadge active={product.ecommerce_active} label="Ecom" />
                        <ActivationBadge active={product.catalog_active} label="Cat" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canPublish && product.status === 'draft' && (
                          <button
                            onClick={() => handleStatusChange(product._id, 'active')}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Publicar"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canPublish && product.status === 'active' && (
                          <button
                            onClick={() => handleStatusChange(product._id, 'inactive')}
                            className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded"
                            title="Desactivar"
                          >
                            <ArchiveBoxIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canUpdate && (
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-1.5 text-vida-600 hover:bg-vida-50 rounded"
                            title="Editar"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(product._id)}
                            disabled={deletingId === product._id}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            title="Eliminar"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Página {page} de {pages} — {total} productos
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Anterior
              </button>
              <button
                onClick={() => setPage(Math.min(pages, page + 1))}
                disabled={page === pages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Siguiente
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {importOpen && (
        <ImportProductsModal
          onClose={() => setImportOpen(false)}
          onImported={() => loadProducts()}
        />
      )}
    </div>
  );
}
